import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import DateRangePicker from '../finance/components/DateRangePicker';
import { getDatePresetLabel } from '../finance/data/mockData';

// AdminPaymentBible — payment-first. One row per PAYMENT (the Stripe charge),
// successful or failed, with its method (Beu card reader, card on file,
// payment link, Klarna, Clearpay, online deposit). Expanding a payment shows
// the state of every transfer that hangs off it — sent, deliberately skipped
// (with the reason), or failed (with the raw Stripe error) — plus the booking
// and deep links to Stripe for confirmation.

const METHOD_LABEL = {
  card_reader: 'Beu card reader',
  card_on_file: 'Card on file',
  payment_link: 'Payment link',
  klarna: 'Klarna',
  clearpay: 'Clearpay',
  online_deposit: 'Online deposit',
  instant: 'Instant withdrawal ⚡',
  standard: 'Standard withdrawal',
};

const CHANNEL_LABEL = {
  pos: 'Till / card reader', deposit: 'Online deposit', payment_link: 'Payment link',
  klarna: 'Klarna', clearpay: 'Clearpay', gift_card: 'Gift card',
  product_order: 'Shop order', quickpay: 'Quick Pay', no_show: 'No-show fee',
  cancellation: 'Cancellation fee', payout: 'Withdrawal', topup: 'Credit top-up',
  subscription: 'Subscription', manual: 'Manual (admin)', unknown: '—',
};

const LEG_LABEL = {
  transfer: 'Transfer sent',
  // leg === 'reconcile' rows get relabelled in renderLeg.
  transfer_failed: 'TRANSFER FAILED',
  transfer_skipped: 'Transfer skipped',
  clawback: 'Clawback (Beu recovered fee)',
  clawback_failed: 'CLAWBACK FAILED',
  refund: 'Refund',
  refund_failed: 'REFUND FAILED',
  transfer_reversal: 'Transfer reversed',
  transfer_reversal_failed: 'REVERSAL FAILED',
  payout: 'Payout to bank',
  payout_failed: 'PAYOUT FAILED',
  charge: 'Payment captured',
  charge_failed: 'CAPTURE FAILED',
};

const SKIP_LABEL = {
  fee_netted: 'withdrawal fee netted off',
  negative_leg: 'negative leg — clawed back instead',
  no_connect_account: 'no Stripe account on file',
  zero_amount: 'zero amount',
};

const PAGE_SIZE = 25;
const REFRESH_MS = 30000;

const money = (pence, currency) => (pence == null ? '—'
  : `${(currency || 'GBP').toUpperCase() === 'GBP' ? '£' : (currency || '').toUpperCase() + ' '}${(pence / 100).toFixed(2)}`);

const stripePaymentUrl = (p) => {
  if (p.paymentIntentId) return `https://dashboard.stripe.com/payments/${p.paymentIntentId}`;
  if (p.chargeId) return `https://dashboard.stripe.com/payments/${p.chargeId}`;
  return null;
};

const stripeLegUrl = (e) => {
  if (e.transferId) return `https://dashboard.stripe.com/connect/transfers/${e.transferId}`;
  if (e.payoutId && e.connectAccountId) return `https://dashboard.stripe.com/connect/accounts/${e.connectAccountId}/activity`;
  if (e.paymentIntentId) return `https://dashboard.stripe.com/payments/${e.paymentIntentId}`;
  if (e.chargeId) return `https://dashboard.stripe.com/payments/${e.chargeId}`;
  return null;
};

class AdminPaymentBible extends Component {
  state = {
    payments: [], total: 0, page: 0, loading: true, error: null,
    problemsOnly: false, channel: '',
    search: '',
    reconciling: null, // ledger event id mid-reconcile
    headlines: null,
    dateStart: (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })(),
    dateEnd: (() => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; })(),
    datePreset: 'today',
    datePickerOpen: false,
    expanded: null, // payment key currently expanded
  };

  componentDidMount() {
    this.fetchPayments(0, false);
    this.timer = setInterval(() => {
      if (this.state.page === 0 && !this.state.expanded) this.fetchPayments(0, false, true);
    }, REFRESH_MS);
  }

  componentWillUnmount() { clearInterval(this.timer); clearTimeout(this.searchTimer); }

  fetchPayments = async (page = 0, append = false, silent = false) => {
    if (!silent) this.setState({ loading: true, error: null });
    try {
      const range = {
        from: this.state.dateStart ? this.state.dateStart.toISOString() : undefined,
        to: this.state.dateEnd ? this.state.dateEnd.toISOString() : undefined,
      };
      const [res, hRes] = await Promise.all([
        adminService.getPaymentBiblePayments({
          page, limit: PAGE_SIZE,
          problemsOnly: this.state.problemsOnly || undefined,
          channel: this.state.channel || undefined,
          search: this.state.search.trim() || undefined,
          ...range,
        }),
        adminService.getPaymentBibleHeadlines(range).catch(() => null),
      ]);
      if (hRes) {
        this.setState({ headlines: (hRes && hRes.responseData) || null });
      }
      const data = (res && res.responseData) || res || {};
      this.setState((prev) => ({
        payments: append ? [...prev.payments, ...(data.payments || [])] : (data.payments || []),
        total: data.total || 0, page, loading: false,
      }));
    } catch (e) {
      console.error('Error fetching payment bible:', e);
      this.setState({ loading: false, error: 'Failed to load the payment ledger' });
    }
  };

  setProblemsOnly = (problemsOnly) => this.setState({ problemsOnly, expanded: null }, () => this.fetchPayments(0, false));
  setChannel = (channel) => this.setState({ channel, expanded: null }, () => this.fetchPayments(0, false));

  onSearchChange = (search) => {
    this.setState({ search, expanded: null });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.fetchPayments(0, false), 400);
  };

  methodLabel(p) {
    if (p.method && METHOD_LABEL[p.method]) return METHOD_LABEL[p.method];
    return CHANNEL_LABEL[p.channel] || p.channel || '—';
  }

  renderPaymentChip(p) {
    const failed = p.status === 'failed';
    const stalled = p.status === 'stalled';
    return (
      <span style={{
        padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        background: (failed || stalled) ? '#FEF3F2' : '#ECFDF3', color: (failed || stalled) ? '#b42318' : '#067647',
      }}>
        {failed
          ? (p.channel === 'payout' ? 'WITHDRAWAL FAILED' : 'PAYMENT FAILED')
          : stalled ? 'CAPTURED — NO TRANSFERS'
          : p.channel === 'payout' ? 'Withdrawal' : 'Payment'}
      </span>
    );
  }

  renderTransferSummary(p) {
    const s = p.summary || {};
    const bits = [];
    if (s.sent) bits.push(<span key="s" style={{ color: '#067647' }}>{s.sent} sent</span>);
    if (s.skipped) bits.push(<span key="k" style={{ color: '#B54708' }}>{s.skipped} skipped</span>);
    if (s.failed) bits.push(<span key="f" style={{ color: '#b42318', fontWeight: 700 }}>{s.failed} FAILED</span>);
    if (s.repaired) bits.push(<span key="r" style={{ color: '#067647', fontWeight: 600 }}>{s.repaired} repaired ✓</span>);
    if (!bits.length) {
      return p.stalled
        ? <span style={{ color: '#b42318', fontWeight: 700 }}>NONE — money still on platform</span>
        : <span style={{ color: '#98A2B3' }}>no transfers yet</span>;
    }
    return bits.reduce((acc, el, i) => (i === 0 ? [el] : [...acc, <span key={`sep${i}`}> · </span>, el]), []);
  }

  reconcile = async (e, p) => {
    const who = e.payeeName || 'the payee';
    const amt = money(e.amount, e.currency);
    // Money-moving action: explicit confirm, amount + destination named.
    const sure = window.confirm(
      `Reconcile this failed transfer?\n\nThis will transfer ${amt} from Beu's platform balance to ${who}'s connect account.\n\nThis cannot be undone.`
    );
    if (!sure) return;
    this.setState({ reconciling: e._id });
    try {
      const res = await adminService.reconcilePaymentBibleTransfer(e._id);
      const data = (res && res.responseData) || {};
      if (data.reconciled) {
        await this.fetchPayments(0, false, true);
        this.setState({ expanded: p.key });
      } else {
        window.alert('Reconcile did not complete — check the ledger.');
      }
    } catch (err) {
      window.alert(`Reconcile failed: ${err && err.message ? err.message : err}`);
    } finally {
      this.setState({ reconciling: null });
    }
  };

  renderLeg(e, p) {
    const isProblem = /_failed$/.test(e.eventType);
    const isSkip = e.eventType === 'transfer_skipped';
    return (
      <div key={e._id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
          <span style={{ color: '#98A2B3', whiteSpace: 'nowrap' }}>{new Date(e.createdAt).toLocaleTimeString('en-GB')}</span>
          <span style={{
            padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            background: isProblem ? '#FEF3F2' : isSkip ? '#FFFAEB' : '#ECFDF3',
            color: isProblem ? '#b42318' : isSkip ? '#B54708' : '#067647',
          }}>
            {e.leg === 'reconcile' ? 'Repair transfer (admin)' : (LEG_LABEL[e.eventType] || e.eventType)}
          </span>
          {e.leg && <span style={{ color: '#667085' }}>{e.leg}</span>}
          <span style={{ fontWeight: 600 }}>{money(e.amount, e.currency)}</span>
          {e.payeeName && <span style={{ color: '#344054' }}>→ {e.payeeName}</span>}
          {stripeLegUrl(e) && (
            <a href={stripeLegUrl(e)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>Stripe ↗</a>
          )}
          {e.eventType === 'transfer_failed' && !e.reconciled && (
            <button type="button"
              disabled={this.state.reconciling === e._id}
              onClick={(ev) => { ev.stopPropagation(); this.reconcile(e, p); }}
              style={{
                marginLeft: 'auto', padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                border: '1px solid #b42318', background: '#fff', color: '#b42318',
                cursor: this.state.reconciling === e._id ? 'default' : 'pointer',
                opacity: this.state.reconciling === e._id ? 0.5 : 1,
              }}>
              {this.state.reconciling === e._id ? 'Reconciling…' : 'Reconcile'}
            </button>
          )}
          {e.eventType === 'transfer_failed' && e.reconciled && (
            <span style={{
              marginLeft: 'auto', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              background: '#ECFDF3', color: '#067647',
            }}
              title={e.reconcileTransferId ? `Repair transfer ${e.reconcileTransferId}` : 'Reconciled'}
            >Reconciled ✓</span>
          )}
        </div>
        {e.skipReason && (
          <div style={{ marginTop: 4, marginLeft: 66, fontSize: 12, color: '#B54708' }}>
            Why: {SKIP_LABEL[e.skipReason] || e.skipReason}
            {e.feeNetted != null && ` — ${money(e.feeNetted, e.currency)} of pending withdrawal fees deducted`}
          </div>
        )}
        {e.error && (
          <div style={{ marginTop: 4, marginLeft: 66, fontSize: 12, color: '#b42318' }}>
            {e.error.code ? `[${e.error.code}] ` : ''}{e.error.message}
          </div>
        )}
      </div>
    );
  }

  renderExpanded(p) {
    return (
      <tr key={`${p.key}-detail`}>
        <td colSpan={9} style={{ background: '#fafafa', padding: 16 }}>
          {p.error && (
            <div style={{ marginBottom: 12, padding: 10, background: '#FEF3F2', borderRadius: 8, color: '#b42318', fontSize: 13 }}>
              <strong>Payment error{p.error.code ? ` (${p.error.code})` : ''}:</strong> {p.error.message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            {p.booking && (
              <div style={{ minWidth: 240 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Booking</div>
                <table style={{ fontSize: 13, borderSpacing: 0 }}>
                  <tbody>
                    <tr><td style={{ color: '#667085', paddingRight: 12 }}>Client</td><td>{p.booking.clientName || '—'}</td></tr>
                    <tr><td style={{ color: '#667085', paddingRight: 12 }}>Stylist</td><td>{p.booking.stylistName || '—'}</td></tr>
                    <tr><td style={{ color: '#667085', paddingRight: 12 }}>Date</td><td>{p.booking.date ? new Date(p.booking.date).toLocaleString('en-GB') : '—'}</td></tr>
                    <tr><td style={{ color: '#667085', paddingRight: 12 }}>Booking total</td><td>£{Number(p.booking.total || 0).toFixed(2)}</td></tr>
                    <tr><td style={{ color: '#667085', paddingRight: 12 }}>Booking id</td>
                      <td>
                        <span
                          title="Click to copy"
                          onClick={() => navigator.clipboard && navigator.clipboard.writeText(String(p.bookingId))}
                          style={{ fontFamily: 'Menlo, monospace', fontSize: 12, cursor: 'copy' }}
                        >{p.bookingId}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {p.booking && p.booking.split && (
              <div style={{ minWidth: 220 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Split &amp; fees</div>
                <table style={{ fontSize: 13, borderSpacing: 0 }}>
                  <tbody>
                    {/* Commission only exists for self-employed-in-salon
                        splits. Owners keep the full amount — the booking
                        stores a negative internal value on this field for
                        them, which is bookkeeping, not money to display.
                        stylistType (owner | self_employed | employed) picks
                        the honest wording for a zero. */}
                    {p.booking.split.commissionRate > 0 && (
                      <tr><td style={{ color: '#667085', paddingRight: 12 }}>Commission rate</td>
                        <td>{p.booking.split.commissionRate}%</td></tr>
                    )}
                    {p.booking.split.salonCommission > 0 ? (
                      <tr><td style={{ color: '#667085', paddingRight: 12 }}>Salon commission</td>
                        <td>£{Number(p.booking.split.salonCommission).toFixed(2)}</td></tr>
                    ) : (
                      <tr><td style={{ color: '#667085', paddingRight: 12 }}>Salon commission</td>
                        <td style={{ color: '#98A2B3' }}>{
                          p.booking.stylistType === 'self_employed' ? '— none, self-employed keeps full amount'
                            : p.booking.stylistType === 'employed' ? '— none (employed staff)'
                            : p.booking.stylistType === 'owner' ? '— owner keeps full amount'
                            : '— none'
                        }</td></tr>
                    )}
                    <tr><td style={{ color: '#667085', paddingRight: 12 }}>Beu fee</td>
                      <td>£{Number(p.booking.split.beuFee).toFixed(2)}</td></tr>
                    <tr><td style={{ color: '#667085', paddingRight: 12 }}>Stripe fee</td>
                      <td>£{Number(p.booking.split.stripeFee).toFixed(2)}</td></tr>
                    {p.booking.split.payoutFeesNetted > 0 && (
                      <tr><td style={{ color: '#B54708', paddingRight: 12 }}>Withdrawal fees netted</td>
                        <td style={{ color: '#B54708' }}>£{Number(p.booking.split.payoutFeesNetted).toFixed(2)}</td></tr>
                    )}
                    {p.booking.split.tip > 0 && (
                      <tr><td style={{ color: '#667085', paddingRight: 12 }}>Tip</td>
                        <td>£{Number(p.booking.split.tip).toFixed(2)}</td></tr>
                    )}
                    {p.booking.split.deposit > 0 && (
                      <tr><td style={{ color: '#667085', paddingRight: 12 }}>Deposit</td>
                        <td>£{Number(p.booking.split.deposit).toFixed(2)}</td></tr>
                    )}
                    {(() => {
                      // Reconciliation line: what came in minus what went out.
                      // Covers Beu + Stripe fees and any withdrawal-fee netting —
                      // if this doesn't roughly equal their sum, something's off
                      // and the legs above will show what.
                      const legsOut = (p.legs || [])
                        .filter((e) => e.eventType === 'transfer')
                        .reduce((sum, e) => sum + (e.amount || 0), 0);
                      if (p.amount == null) return null;
                      const retained = p.amount - legsOut;
                      return (
                        <tr><td style={{ color: '#344054', paddingRight: 12, fontWeight: 600 }}>Retained of this payment</td>
                          <td style={{ fontWeight: 600 }}>{money(retained, p.currency)}</td></tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ flex: 1, minWidth: 340 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Transfers under this payment</div>
              {(p.legs && p.legs.length)
                ? p.legs.map((e) => this.renderLeg(e, p))
                : <div style={{ fontSize: 13, color: '#98A2B3', padding: '8px 0' }}>None recorded (yet).</div>}
            </div>
          </div>
        </td>
      </tr>
    );
  }

  render() {
    const { payments, total, page, loading, error, problemsOnly, channel, expanded } = this.state;
    return (
      <AdminWrapper title="Payment Bible">
        <div className="admin-page-header">
          <div>
            <h1 className="admin-page-title">Payment Bible</h1>
            <p className="admin-page-subtitle">
              One row per payment — Beu card reader, card on file, payment link, Klarna, Clearpay,
              online deposit. Expand a payment to see every transfer under it: sent, skipped (with
              the reason) or failed (with the Stripe error).
            </p>
          </div>
        </div>

        {this.state.headlines && (() => {
          const h = this.state.headlines;
          const tile = (label, value, sub, tone) => (
            <div key={label} style={{
              flex: '1 1 140px', minWidth: 140, padding: '12px 16px', borderRadius: 12,
              border: '1px solid #ececec', background: '#fff',
            }}>
              <div style={{ fontSize: 12, color: '#667085' }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: tone || '#101828', marginTop: 2 }}>{value}</div>
              {sub && <div style={{ fontSize: 12, color: '#98A2B3', marginTop: 2 }}>{sub}</div>}
            </div>
          );
          return (
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              {tile('Payments in', money(h.payments.amount), `${h.payments.count} payments`)}
              {tile('Transfers out', money(h.transfers.amount), `${h.transfers.count} sent`)}
              {tile('Failed (open)', h.failed.count ? money(h.failed.amount) : '—',
                h.failed.count ? `${h.failed.count} need attention` : 'none',
                h.failed.count ? '#b42318' : undefined)}
              {tile('Skipped', h.skipped.count ? money(h.skipped.amount) : '—',
                h.skipped.count ? `${h.skipped.count} with reasons` : 'none',
                h.skipped.count ? '#B54708' : undefined)}
              {tile('Withdrawals ⚡ instant', money(h.withdrawals.instant.amount), `${h.withdrawals.instant.count} payouts`)}
              {tile('Withdrawals standard', money(h.withdrawals.standard.amount), `${h.withdrawals.standard.count} payouts`)}
              {h.beuFees && tile('Beu fees', money(h.beuFees.total),
                `£${(h.beuFees.bookings / 100).toFixed(2)} bookings + £${(h.beuFees.withdrawals / 100).toFixed(2)} withdrawals`)}
              {tile('Retained (platform)', money(h.retained.amount), h.repaired.count ? `after ${h.repaired.count} repairs` : 'in − out, per payment')}
            </div>
          );
        })()}

        <div className="admin-filters" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => this.setState({ datePickerOpen: !this.state.datePickerOpen })}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: '1px solid #e0e0e0', background: '#fff', color: '#344054',
              }}>
              {getDatePresetLabel(this.state.datePreset)} ▾
            </button>
            <DateRangePicker
              isOpen={this.state.datePickerOpen}
              onClose={() => this.setState({ datePickerOpen: false })}
              startDate={this.state.dateStart}
              endDate={this.state.dateEnd}
              selectedPreset={this.state.datePreset}
              onPresetChange={(pr) => this.setState({ datePreset: pr })}
              onApply={(start, end) => {
                const s2 = new Date(start); s2.setHours(0, 0, 0, 0);
                const e2 = new Date(end); e2.setHours(23, 59, 59, 999);
                this.setState({ dateStart: s2, dateEnd: e2, datePickerOpen: false, expanded: null },
                  () => this.fetchPayments(0, false));
              }}
            />
          </div>
          {[[false, 'All payments'], [true, 'Problems only']].map(([val, label]) => (
            <button key={String(val)} type="button" onClick={() => this.setProblemsOnly(val)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: '1px solid ' + (problemsOnly === val ? '#29282D' : '#e0e0e0'),
                background: problemsOnly === val ? '#29282D' : '#fff',
                color: problemsOnly === val ? '#fff' : '#444',
              }}>{label}</button>
          ))}
          <select value={channel} onChange={(ev) => this.setChannel(ev.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, fontSize: 13, border: '1px solid #e0e0e0' }}>
            <option value="">All channels</option>
            {Object.keys(CHANNEL_LABEL).filter((c) => c !== 'unknown').map((c) => (
              <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>
            ))}
          </select>
          <input
            type="text"
            value={this.state.search}
            onChange={(ev) => this.onSearchChange(ev.target.value)}
            placeholder="Search booking id or Stripe id (pi_… ch_… tr_…)"
            style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, border: '1px solid #e0e0e0', minWidth: 300 }}
          />
          <span style={{ alignSelf: 'center', fontSize: 12, color: '#98A2B3' }}>
            {total} payments · refreshes every 30s
          </span>
        </div>

        {error && <div style={{ color: '#b42318', marginBottom: 12 }}>{error}</div>}

        {loading && payments.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>
        ) : payments.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#667085', background: '#fafafa', borderRadius: 12 }}>
            <strong>No payments{problemsOnly ? ' with problems' : ''} yet</strong>
            <div style={{ fontSize: 13, marginTop: 6 }}>
              Rows appear the moment money moves — or deliberately doesn't.
            </div>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Booking</th>
                  <th>Client</th>
                  <th>Salon</th>
                  <th>Amount</th>
                  <th>Transfers</th>
                  <th>Stripe</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => ([
                  <tr key={p.key} onClick={() => this.setState({ expanded: expanded === p.key ? null : p.key })}
                    style={{ cursor: 'pointer', background: expanded === p.key ? '#f5f5f5' : undefined }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{p.at ? new Date(p.at).toLocaleString('en-GB') : '—'}</td>
                    <td>{this.renderPaymentChip(p)}</td>
                    <td>
                      {this.methodLabel(p)}
                      {p.booking && p.booking.split && p.booking.split.deposit > 0 && p.channel !== 'deposit' && (
                        <span style={{
                          marginLeft: 8, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                          background: '#EFF8FF', color: '#175CD3', whiteSpace: 'nowrap',
                        }}
                          title={`A £${Number(p.booking.split.deposit).toFixed(2)} deposit was taken on this booking — this payment is the remainder.`}
                        >£{Number(p.booking.split.deposit).toFixed(2)} deposit taken</span>
                      )}
                    </td>
                    <td onClick={(ev) => ev.stopPropagation()}>
                      {p.bookingId ? (
                        <span
                          title={`${p.bookingId} — click to copy`}
                          onClick={() => navigator.clipboard && navigator.clipboard.writeText(String(p.bookingId))}
                          style={{ fontFamily: 'Menlo, monospace', fontSize: 12, cursor: 'copy' }}
                        >…{String(p.bookingId).slice(-6)}</span>
                      ) : '—'}
                    </td>
                    <td>{(p.booking && p.booking.clientName) || '—'}</td>
                    <td>{p.salonName || '—'}</td>
                    <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{money(p.amount, p.currency)}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{this.renderTransferSummary(p)}</td>
                    <td onClick={(ev) => ev.stopPropagation()}>
                      {stripePaymentUrl(p)
                        ? <a href={stripePaymentUrl(p)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>Confirm ↗</a>
                        : '—'}
                    </td>
                  </tr>,
                  expanded === p.key ? this.renderExpanded(p) : null,
                ]))}
              </tbody>
            </table>
            {payments.length < total && (
              <div style={{ padding: 12, textAlign: 'center' }}>
                <button type="button" onClick={() => this.fetchPayments(page + 1, true)}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                  Load more
                </button>
              </div>
            )}
          </div>
        )}
      </AdminWrapper>
    );
  }
}

export default AdminPaymentBible;
